import React, { lazy, Suspense } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { Loader2 } from 'lucide-react';
import { HeaderWithLogout } from '@/components/ui/header-with-logout';
import { getAccountBrand } from '@shared/branding';

const NeighborhoodBackground = lazy(() =>
  import('@/components/neighborhood-bg').then(m => ({ default: m.NeighborhoodBackground }))
);

interface RoleBasedLayoutProps {
  children: React.ReactNode;
  pageTitle?: string;
}

export function RoleBasedLayout({ children, pageTitle }: RoleBasedLayoutProps) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center p-6 bg-[#070d1c] border border-cyan-800/30 rounded-lg max-w-md">
          <h2 className="text-xl font-semibold text-white mb-2">Authentication Required</h2>
          <p className="text-cyan-200/70">
            Please log in to access this page. This area requires authentication.
          </p>
        </div>
      </div>
    );
  }

  const accountBrand = getAccountBrand(user);
  const isDealnet = accountBrand === 'dealnet';
  const isRoadMover = accountBrand === 'roadmover';

  return (
    <div className={`flex flex-col min-h-screen ${isRoadMover ? 'roadmover-workspace' : ''}`} data-brand={accountBrand} style={{ position: 'relative' }}>
      {isDealnet && (
        <Suspense fallback={null}>
          <NeighborhoodBackground />
        </Suspense>
      )}
      <div className={`${isDealnet ? 'dealnet-light' : ''} ${isRoadMover ? 'roadmover-account roadmover-workspace' : ''}`} style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <HeaderWithLogout title={pageTitle || (isRoadMover ? 'RoadMover' : undefined)} />
        <main className="flex-grow">
          {children}
        </main>
      </div>
    </div>
  );
}
